import { permissionApi, User, Department } from '../services/permissionApi';
import i18next from 'i18next';
import { create } from 'zustand';
import { UserInfo } from '@mygpt/app/apis/services/robots';
import { useAdminStore } from '@/store/admin';

// 权限等级定义
export enum PermissionLevel {
  OWNER = 'ROLE_COMPANY_OWNER', // 公司所有者
  ADMIN = 'ROLE_ADMIN', // 公司管理员
  DEPT_ADMIN = 'ROLE_ADMIN_DEPARTMENT', // 部门管理员
  EDITOR = 'ROLE_EDITOR', // 编辑者
  VIEWER = 'ROLE_VIEWER', // 查看者
}

export interface PermissionControlResult {
  // 当前用户信息
  currentUser: User | null;
  currentUserRole: string | null;

  // 权限判断
  isOwner: boolean; // 是否为公司所有者
  isAdmin: boolean; // 是否为公司管理员（包括所有者）
  isDeptAdmin: boolean; // 是否为部门管理员（包括公司级管理员）
  isEditor: boolean; // 是否为编辑者（包括所有管理员）
  loadCurrentUserInfo: (userInfo?: UserInfo) => Promise<void>;

  // 部门权限判断
  canManageDepartment: (department: Department | any) => boolean;
  canManageUser: (user: User | any) => boolean;

  // 操作权限
  canCreateDepartment: (parentDepartment?: Department | any) => boolean;
  canEditDepartment: (department: Department | any) => boolean;
  canDeleteDepartment: (department: Department | any) => boolean;
  canCreateUser: () => boolean;
  canEditUser: (user: User | any) => boolean;
  canDeleteUser: (user: User | any) => boolean;
  canResetPassword: (user: User | any) => boolean;
  canManageApiKeys: (user: User | any) => boolean;
  canSetUserStatus: (user: User | any) => boolean;
  canAssignUserToDepartment: (user: User | any, department?: Department | any) => boolean;
}

// 获取权限等级数值（数值越高权限越大）
const getPermissionLevel = (roleCode: string): number => {
  switch (roleCode) {
    case PermissionLevel.OWNER:
      return 5; // 公司拥有者
    case PermissionLevel.ADMIN:
      return 4; // 公司管理员
    case PermissionLevel.DEPT_ADMIN:
      return 3; // 部门管理员
    case PermissionLevel.EDITOR:
      return 2; // 编辑者
    case PermissionLevel.VIEWER:
      return 1; // 查看者
    default:
      return 0; // 未知角色
  }
};

export interface PermissionControlState {
  currentUser: User | null;
  currentUserRole: string | null;
  userDepartmentId: string | null;
  userDepartmentPath: string[];

  setCurrentUser: (user: User | null) => void;
  setCurrentUserRole: (role: string | null) => void;
  setUserDepartmentId: (deptId: string | null) => void;
  setUserDepartmentPath: (path: string[]) => void;
}

// 创建 Zustand store
const usePermissionControlStore = create<PermissionControlState>((set) => ({
  currentUser: null,
  currentUserRole: null,
  userDepartmentId: null,
  userDepartmentPath: [],

  setCurrentUser: (user: User | null) => set({ currentUser: user }),
  setCurrentUserRole: (role: string | null) => set({ currentUserRole: role }),
  setUserDepartmentId: (deptId: string | null) => set({ userDepartmentId: deptId }),
  setUserDepartmentPath: (path: string[]) => set({ userDepartmentPath: path }),
}));

export const usePermissionControl = (): PermissionControlResult => {
  const {
    currentUser,
    currentUserRole,
    userDepartmentId,
    setCurrentUser,
    setCurrentUserRole,
    setUserDepartmentId,
    setUserDepartmentPath,
  } = usePermissionControlStore();

  // 加载当前用户的详细信息
  const loadCurrentUserInfo = async (userInfo?: UserInfo) => {
    if (!userInfo?.email) return;

    try {
      // 先获取部门信息
      const departments = await permissionApi.getOrganizationStructure();

      // 获取用户列表，通过OGS userId匹配当前用户（email可能带OAuth前缀导致不匹配）
      const { users } = await permissionApi.getUserList();
      const { userOgsId } = useAdminStore.getState();
      const currentUserData = userOgsId
        ? users.find((u) => u.id === userOgsId)
        : users.find((u) => u.email === userInfo.email);

      console.log('当前用户信息:', {
        userInfo,
        currentUserData,
        departmentId: currentUserData?.departmentId,
        departmentPath: currentUserData?.departmentPath,
        roleCode: currentUserData?.roleCode,
      });

      if (currentUserData) {
        // 如果用户数据中没有部门路径，尝试从部门ID构建路径
        let finalDepartmentPath = currentUserData.departmentPath;

        if (!finalDepartmentPath && currentUserData.departmentId) {
          // 构建部门路径
          const buildPath = (deptId: string, depts: any[], path: string[] = []): string => {
            for (const dept of depts) {
              if (String(dept.id) === String(deptId)) {
                return [...path, dept.name].join(' > ');
              }
              if (dept.children) {
                const result = buildPath(deptId, dept.children, [...path, dept.name]);
                if (result) return result;
              }
            }
            return '';
          };

          // 获取公司名称
          const companyInfo = await permissionApi.getCurrentCompanyInfo();
          const companyName = companyInfo?.name || i18next.t('adminConsole.newConsole.common.defaultCompanyName');

          // 构建完整路径
          const deptPath = buildPath(currentUserData.departmentId, departments);
          if (deptPath) {
            finalDepartmentPath = `${companyName} > ${deptPath}`;
          }

          console.log('构建的部门路径:', finalDepartmentPath);
        }

        // 更新用户信息，确保包含部门路径
        const updatedUserData = {
          ...currentUserData,
          departmentPath: finalDepartmentPath || currentUserData.departmentPath,
        };

        console.log('设置当前用户数据:', {
          originalUserData: currentUserData,
          finalDepartmentPath,
          updatedUserData,
        });

        setCurrentUser(updatedUserData);
        setCurrentUserRole(currentUserData.roleCode || null);
        setUserDepartmentId(currentUserData.departmentId || null);

        // 解析部门路径
        if (finalDepartmentPath) {
          const pathArray = finalDepartmentPath.split(' > ').filter((p) => p);
          setUserDepartmentPath(pathArray);
        }
      }
    } catch (error) {
      console.error('获取当前用户权限信息失败:', error);
    }
  };

  // 判断用户角色
  const isOwner = currentUserRole === PermissionLevel.OWNER;
  const isAdmin = currentUserRole === PermissionLevel.ADMIN || isOwner;
  const isDeptAdmin = currentUserRole === PermissionLevel.DEPT_ADMIN || isAdmin;
  const isEditor = currentUserRole === PermissionLevel.EDITOR || isDeptAdmin;

  // 获取部门的完整路径
  const getDepartmentPath = (department: Department | any): string => {
    if (!department) return '';

    // 如果是根节点
    if (department.id === 'root') {
      return 'root';
    }

    // 如果有 departmentPath 字段（通常是用户对象）
    if (department.departmentPath) {
      return department.departmentPath;
    }

    // 如果是组织树节点，需要构建完整路径
    // 这里我们需要从组织树结构中构建路径
    // 由于组织树节点可能没有完整路径，我们使用名称作为后备
    if (department.name) {
      return department.name;
    }

    return '';
  };

  // 检查当前用户是否可以管理指定部门
  const canManageDepartment = (department: Department | any): boolean => {
    // 公司所有者可以管理所有部门
    if (isOwner) return true;

    // 公司管理员可以管理所有部门（除了根节点）
    if (isAdmin) {
      if (!department) return false;
      // 如果是根节点，管理者不能管理
      if (department.id === 'root') return false;
      return true;
    }

    // 部门管理员只能管理本层级及下级部门
    if (currentUserRole === PermissionLevel.DEPT_ADMIN && userDepartmentId) {
      if (!department) return false;

      // 如果是根节点，部门管理员不能管理
      if (department.id === 'root') return false;

      // 管理者可以管理自己所在部门
      if (String(department.id) === String(userDepartmentId)) return true;

      // 对于组织树节点，使用递归检查父级关系
      const checkParentRelation = (dept: any): boolean => {
        if (!dept) return false;

        // 如果有原始数据，检查父ID
        if (dept.originalData) {
          const parentId = dept.originalData.parentId;
          if (parentId && String(parentId) === String(userDepartmentId)) {
            return true;
          }
        }

        // 如果有父节点引用，递归检查
        if (dept.parent) {
          if (String(dept.parent.id) === String(userDepartmentId)) {
            return true;
          }
          return checkParentRelation(dept.parent);
        }

        return false;
      };

      // 检查直接父级关系
      if (checkParentRelation(department)) {
        return true;
      }

      // 如果有部门路径，检查路径关系
      const deptPath = getDepartmentPath(department);
      if (currentUser?.departmentPath && deptPath) {
        // 检查部门路径是否包含用户部门名称
        // 或者部门路径以用户部门路径开头
        const userDeptParts = currentUser.departmentPath.split(' > ');
        const targetDeptParts = deptPath.split(' > ');

        // 检查目标部门是否是用户部门的子部门
        // 即目标部门路径包含完整的用户部门路径
        if (targetDeptParts.length > userDeptParts.length) {
          const isSubDept = userDeptParts.every((part, index) => targetDeptParts[index] === part);
          if (isSubDept) return true;
        }
      }

      return false;
    }

    // 编辑者和查看者没有部门管理权限
    return false;
  };

  // 检查当前用户是否可以管理指定用户
  const canManageUser = (user: User | any): boolean => {
    // console.log('检查用户管理权限:', {
    //   targetUser: user?.email,
    //   currentUserEmail: currentUser?.email,
    //   currentUserRole,
    //   isOwner,
    //   isAdmin,
    //   userDepartmentId,
    //   currentUserPath: currentUser?.departmentPath,
    //   targetUserPath: user?.departmentPath,
    // });

    // 不能管理自己
    if (user.email === currentUser?.email) {
      //   console.log('不能管理自己');
      return false;
    }

    // 权限等级检查：不能管理权限等级高于或等于自己的用户
    const currentUserLevel = getPermissionLevel(currentUserRole || '');
    const targetUserLevel = getPermissionLevel(user.roleCode || '');

    // 不能管理权限等级高于自己的用户
    if (targetUserLevel > currentUserLevel) {
      return false;
    }

    // 对于同等级用户的特殊处理
    if (targetUserLevel === currentUserLevel) {
      // 部门管理员不能管理同部门的其他部门管理员
      if (currentUserRole === PermissionLevel.DEPT_ADMIN && user.roleCode === PermissionLevel.DEPT_ADMIN) {
        // 检查是否为同一部门
        const currentUserDeptId = currentUser?.departmentId;
        const targetUserDeptId = user.departmentId;

        if (currentUserDeptId === targetUserDeptId) {
          return false;
        }
      }

      // 公司管理员不能管理公司拥有者
      if (currentUserRole === PermissionLevel.ADMIN && user.roleCode === PermissionLevel.OWNER) {
        // console.log('公司管理员不能管理公司拥有者');
        return false;
      }
    }

    // 拥有者可以管理所有用户（在权限等级检查通过后）
    if (isOwner) {
      //   console.log('用户是拥有者，可以管理');
      return true;
    }

    // 公司管理员可以管理所有用户，部门管理员只能管理本层级及下级部门的用户
    if (isAdmin) {
      //   console.log('公司管理员可以管理所有用户');
      return true;
    }

    if (currentUserRole === PermissionLevel.DEPT_ADMIN) {
      //   console.log('用户是部门管理员');

      // 部门管理员不能管理未分配部门的用户
      if (!user.departmentId) {
        // console.log('目标用户无部门，部门管理员不能管理');
        return false;
      }

      // 如果部门管理员没有部门ID，不应该发生，但为安全起见返回false
      if (!userDepartmentId) {
        // console.log('部门管理员无部门ID，异常情况');
        return false;
      }

      // 如果用户在同一部门，需要进一步检查角色
      if (String(user.departmentId) === String(userDepartmentId)) {
        // 如果目标用户也是部门管理员，不能管理同部门的部门管理员
        if (user.roleCode === PermissionLevel.DEPT_ADMIN) {
          // console.log('不能管理同部门的其他部门管理员');
          return false;
        }
        // 可以管理同部门的其他角色用户
        // console.log('目标用户在同一部门且非部门管理员，可以管理');
        return true;
      }

      // 检查用户是否在下级部门
      if (currentUser?.departmentPath && user.departmentPath) {
        // 如果用户的部门路径以管理员的部门路径开头，说明在下级部门
        // 需要确保路径匹配是完整的部门名称匹配，避免部分匹配
        const managerPathParts = currentUser.departmentPath.split(' > ');
        const userPathParts = user.departmentPath.split(' > ');

        // 检查用户部门路径是否包含管理员的完整部门路径
        if (userPathParts.length >= managerPathParts.length) {
          const isSubDepartment = managerPathParts.every((part, index) => userPathParts[index] === part);
          //   console.log('是否为子部门:', isSubDepartment);
          if (isSubDepartment) return true;
        } else {
          // 目标部门路径长度不足，不是子部门
        }
      } else {
        // 缺少部门路径信息
      }
    }

    // console.log('没有管理权限');
    // 编辑者和查看者没有用户管理权限
    return false;
  };

  // 操作权限判断函数
  const canCreateDepartment = (parentDepartment?: Department | any): boolean => {
    if (isOwner) return true;

    // 公司管理员可以在任何部门（包括根节点）下创建子部门
    if (isAdmin) {
      // 如果没有指定父部门，不允许
      if (!parentDepartment) return false;
      return true;
    }

    // 部门管理员只能在自己管理的部门下创建子部门
    if (currentUserRole === PermissionLevel.DEPT_ADMIN) {
      // 如果没有指定父部门，不允许
      if (!parentDepartment) return false;

      // 如果是根节点，部门管理员不能在根节点下创建
      if (parentDepartment.id === 'root') {
        return false;
      }

      // 检查是否可以管理这个父部门
      return canManageDepartment(parentDepartment);
    }

    return false;
  };

  const canEditDepartment = (department: Department | any): boolean => {
    // 不能编辑根节点
    if (department?.id === 'root') return false;

    if (isOwner) return true;

    // 公司管理员可以编辑所有部门，部门管理员可以编辑自己管理范围内的部门
    if (isAdmin) {
      return true;
    }

    if (currentUserRole === PermissionLevel.DEPT_ADMIN) {
      return canManageDepartment(department);
    }

    return false;
  };

  const canDeleteDepartment = (department: Department | any): boolean => {
    // 不能删除根节点
    if (department?.id === 'root') return false;

    if (isOwner) return true;

    // 公司管理员可以删除所有空部门，部门管理员可以删除自己管理范围内的空部门
    if (isAdmin) {
      return true;
    }

    if (currentUserRole === PermissionLevel.DEPT_ADMIN) {
      return canManageDepartment(department);
    }

    return false;
  };

  const canCreateUser = (): boolean => {
    // 公司所有者、公司管理员和部门管理员可以创建用户
    return isDeptAdmin;
  };

  const canEditUser = (user: User | any): boolean => {
    return canManageUser(user);
  };

  const canDeleteUser = (user: User | any): boolean => {
    return canManageUser(user);
  };

  const canResetPassword = (user: User | any): boolean => {
    return canManageUser(user);
  };

  const canManageApiKeys = (user: User | any): boolean => {
    // 拥有者、管理员和部门管理员都可以管理API密钥
    return canManageUser(user);
  };

  const canSetUserStatus = (user: User | any): boolean => {
    return canManageUser(user);
  };

  const canAssignUserToDepartment = (user: User | any, department?: Department | any): boolean => {
    // 检查是否可以管理该用户
    if (!canManageUser(user)) return false;

    // 如果指定了目标部门，检查是否可以管理该部门
    if (department && !canManageDepartment(department)) return false;

    return true;
  };

  return {
    currentUser,
    currentUserRole,
    isOwner,
    isAdmin,
    isDeptAdmin,
    isEditor,
    loadCurrentUserInfo,
    canManageDepartment,
    canManageUser,
    canCreateDepartment,
    canEditDepartment,
    canDeleteDepartment,
    canCreateUser,
    canEditUser,
    canDeleteUser,
    canResetPassword,
    canManageApiKeys,
    canSetUserStatus,
    canAssignUserToDepartment,
  };
};
